import { useEffect, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { FolderOpen, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { fmtDate } from '@/lib/format';

/**
 * Shared Google Drive folder link surface for the Knowledge Hub.
 * Owner + Design Ops (Operations) can set/update/remove; everyone else sees it.
 */
export function DriveLinkCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/knowledge/drive-link');
      setData(r.data);
      setUrl(r.data.url || '');
      setLabel(r.data.label || '');
      setDescription(r.data.description || '');
    } catch (e) { toast.error(parseErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!url.trim()) { toast.error('Google Drive folder URL is required'); return; }
    setSaving(true);
    try {
      await api.put('/knowledge/drive-link', {
        url: url.trim(),
        label: label.trim() || null,
        description: description.trim() || null,
      });
      toast.success('Drive link updated');
      setOpen(false);
      load();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    try {
      await api.delete('/knowledge/drive-link');
      toast.success('Drive link removed');
      setConfirmClear(false);
      load();
    } catch (e) { toast.error(parseErr(e)); }
  };

  if (loading || !data) return null;
  const hasLink = !!data.url;
  const canEdit = !!data.can_edit;

  if (!hasLink && !canEdit) return null;

  return (
    <div className="mb-3" data-testid="knowledge-drive-link-card">
      <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold px-1 py-1">
        Drive folder
      </div>
      {hasLink ? (
        <div className="border rounded-lg p-3 space-y-2"
             style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--card))' }}
             data-testid="knowledge-drive-link">
          <a href={data.url} target="_blank" rel="noreferrer"
             className="flex items-center gap-2 group"
             data-testid="knowledge-drive-open">
            <div className="w-8 h-8 rounded-md flex items-center justify-center flex-none"
                 style={{ background: 'hsl(var(--surface-2))' }}>
              <FolderOpen size={16} className="text-[hsl(var(--primary))]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium truncate group-hover:underline">
                {data.label || 'Knowledge Hub — Google Drive folder'}
              </div>
              <div className="text-[11px] text-[hsl(var(--foreground-subtle))] truncate flex items-center gap-1">
                <ExternalLink size={10} />Open in Drive
              </div>
            </div>
          </a>
          {data.description && (
            <div className="text-[12px] text-[hsl(var(--foreground-muted))] whitespace-pre-wrap">
              {data.description}
            </div>
          )}
          {data.updated_at && (
            <div className="text-[10.5px] text-[hsl(var(--foreground-subtle))]">
              Updated {fmtDate(data.updated_at)} by {data.updated_by}
            </div>
          )}
          {canEdit && (
            <div className="flex items-center gap-1 pt-1">
              <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="knowledge-drive-edit">
                <Pencil size={11} className="mr-1" />Update link
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmClear(true)} data-testid="knowledge-drive-clear">
                <Trash2 size={11} className="mr-1" />Remove
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="border-2 border-dashed rounded-lg p-3 text-center"
             style={{ borderColor: 'hsl(var(--border))' }}>
          <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
            No Drive folder linked yet.
          </div>
          <Button size="sm" variant="outline" className="mt-2" onClick={() => setOpen(true)} data-testid="knowledge-drive-add">
            <FolderOpen size={12} className="mr-1" />Link a Drive folder
          </Button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="knowledge-drive-dialog">
          <DialogHeader>
            <DialogTitle>{hasLink ? 'Update Drive folder link' : 'Link a Google Drive folder'}</DialogTitle>
            <DialogDescription>
              Paste an existing Google Drive folder link. Everyone can then open it in a new tab.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[12px] font-medium">Drive folder URL *</label>
              <Input value={url} onChange={e => setUrl(e.target.value)}
                     placeholder="https://drive.google.com/drive/folders/…"
                     data-testid="knowledge-drive-input-url" />
            </div>
            <div>
              <label className="text-[12px] font-medium">Label (optional)</label>
              <Input value={label} onChange={e => setLabel(e.target.value)}
                     placeholder="e.g. SOP Master"
                     data-testid="knowledge-drive-input-label" />
            </div>
            <div>
              <label className="text-[12px] font-medium">Description (optional)</label>
              <Textarea rows={2} value={description} onChange={e => setDescription(e.target.value)}
                        placeholder="What's in this folder?"
                        data-testid="knowledge-drive-input-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="knowledge-drive-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmClear} onOpenChange={setConfirmClear}>
        <DialogContent data-testid="knowledge-drive-clear-dialog">
          <DialogHeader>
            <DialogTitle>Remove the Drive link?</DialogTitle>
            <DialogDescription>
              Users will no longer see the “Open in Drive” shortcut in the Knowledge Hub. You can re-add it any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmClear(false)}>Cancel</Button>
            <Button variant="destructive" onClick={clear} data-testid="knowledge-drive-clear-confirm">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
